/**
 * Camera and microphone, as one strip under the preview.
 *
 * ## Why this replaced two panels
 *
 * They were two labelled groups, each with a full-width button and a full-width
 * device dropdown - four rows and two headings for two switches. That is a lot
 * of the column's height spent on the two controls a host touches once at the
 * start of a set and then never again, while the things they actually adjust
 * mid-broadcast were pushed below the fold.
 *
 * ## The device chooser is a native select, deliberately
 *
 * It looks like a chevron and it is a real `<select>` sitting invisibly over
 * that chevron. A custom popover would need its own focus trap, its own
 * keyboard handling and its own escape behaviour, and would still be worse on a
 * phone than the thing the operating system already provides.
 *
 * It appears only when there is more than one device to choose between. Device
 * labels are empty until permission has been granted once, so before that a
 * list would be blank entries - and with a single camera there is no choice to
 * offer in the first place.
 */
export function DeviceBar({
  camera,
  microphone,
  cameras,
  microphones,
  cameraOn,
  micOn,
  onToggleCamera,
  onToggleMic,
  onPickCamera,
  onPickMic,
}: {
  camera: string;
  microphone: string;
  cameras: MediaDeviceInfo[];
  microphones: MediaDeviceInfo[];
  cameraOn: boolean;
  micOn: boolean;
  onToggleCamera: () => void;
  onToggleMic: () => void;
  onPickCamera: (id: string) => void;
  onPickMic: (id: string) => void;
}) {
  return (
    <div className="flex items-stretch gap-2" data-studio-devices>
      <Device
        label="Camera"
        on={cameraOn}
        onToggle={onToggleCamera}
        value={camera}
        devices={cameras}
        onPick={onPickCamera}
        /*
         * Spelled out rather than built from a template.
         *
         * These are the handles three e2e suites drive the studio by, and a
         * computed `data-studio-${hook}` renders correctly while being
         * invisible to anyone grepping for the name they saw in a test.
         */
        toggleProps={{ "data-studio-camera": true }}
        pickProps={{ "data-studio-camera-pick": true }}
        icon={
          <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="currentColor" aria-hidden>
            <path d="M2 4.5h7.5v7H2zM10.5 7l3.5-2v6l-3.5-2z" />
          </svg>
        }
      />

      <Device
        label="Mic"
        on={micOn}
        onToggle={onToggleMic}
        value={microphone}
        devices={microphones}
        onPick={onPickMic}
        toggleProps={{ "data-studio-mic": true }}
        pickProps={{ "data-studio-mic-pick": true }}
        icon={
          <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="currentColor" aria-hidden>
            <path d="M8 1.5a2 2 0 012 2v4a2 2 0 11-4 0v-4a2 2 0 012-2z" />
            <path d="M4 7v.5a4 4 0 008 0V7h1.2v.5a5.2 5.2 0 01-4.6 5.17V15H7.4v-2.33A5.2 5.2 0 012.8 7.5V7z" />
          </svg>
        }
      />
    </div>
  );
}

function Device({
  label,
  on,
  onToggle,
  value,
  devices,
  onPick,
  toggleProps,
  pickProps,
  icon,
}: {
  label: string;
  on: boolean;
  onToggle: () => void;
  value: string;
  devices: MediaDeviceInfo[];
  onPick: (id: string) => void;
  toggleProps: Record<string, boolean>;
  pickProps: Record<string, boolean>;
  icon: React.ReactNode;
}) {
  /*
   * The remembered choice, but only while that device exists.
   *
   * A stored id for an unplugged webcam matches no option, which browsers
   * render as blank - so the control would show nothing while the engine
   * quietly used the default. Showing the default is the truth; the preference
   * is still stored and comes back when the device does.
   */
  const selected = devices.some((d) => d.deviceId === value) ? value : "";

  return (
    <div
      className={`flex min-w-0 flex-1 items-stretch overflow-hidden rounded-[var(--radius-control)] border transition-colors ${
        on ? "border-transparent bg-raised" : "border-line"
      }`}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-pressed={on}
        {...toggleProps}
        className={`flex min-w-0 flex-1 items-center justify-center gap-1.5 px-2 py-2 text-[12px] font-bold transition-colors ${
          on ? "text-ink" : "text-soft hover:text-ink"
        }`}
      >
        <span className={on ? "text-accent" : "text-faint"}>{icon}</span>
        {/*
          The state is in the words, not only in the colour. "Mic" and "Mic on"
          are distinguishable without seeing which of two dark greys the strip
          is - which matters here more than most places, because getting it
          wrong means broadcasting silence or broadcasting a room you thought
          was private.
        */}
        <span className="truncate">{on ? `${label} on` : label}</span>
      </button>

      {devices.length > 1 && (
        <div className="relative flex shrink-0 items-center border-l border-line/60">
          <span
            aria-hidden
            className="pointer-events-none px-2 font-mono text-[9px] text-faint"
          >
            ▼
          </span>

          {/*
            The real control, invisible over the chevron. `opacity-0` rather
            than `appearance-none` and styling: the native menu is better than
            anything worth building here, and this keeps it.
          */}
          <select
            value={selected}
            onChange={(event) => onPick(event.target.value)}
            aria-label={`Which ${label.toLowerCase()}`}
            {...pickProps}
            className="absolute inset-0 cursor-pointer opacity-0"
          >
            <option value="">System default</option>
            {devices.map((device) => (
              <option key={device.deviceId} value={device.deviceId}>
                {device.label || "Unnamed device"}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}
